import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@/hooks/use-theme";
import { AppHeader } from "@/components/AppHeader";
import { LoanSetup, type LoanSetupData, type RepaymentMode } from "@/components/LoanSetup";
import { SummaryCards } from "@/components/SummaryCards";
import { GroupedMetrics } from "@/components/GroupedMetrics";
import { LoanHeaderCard } from "@/components/LoanHeaderCard";
import { LoanProgressStrip, type MilestoneStep } from "@/components/LoanProgressStrip";
import { IdeasPanel } from "@/components/IdeasPanel";
import { MilestonesPanel, type Milestone } from "@/components/MilestonesPanel";
import { BalanceChart } from "@/components/BalanceChart";
import { CostComparison } from "@/components/CostComparison";
import { ScheduleTabs } from "@/components/ScheduleTabs";
import { PrintSchedule } from "@/components/PrintSchedule";
import { StressTestAnalysis } from "@/components/StressTestAnalysis";
import { USACarrierNotes } from "@/components/USACarrierNotes";
import {
  StewardshipAndFutureMe,
  emptyStewardship,
  emptyFutureMe,
  normalizeStewardship,
  stewardshipCompleted,
  type StewardshipAuto,
  type StewardshipState,
  type FutureMeState,
} from "@/components/StewardshipAndFutureMe";
import { PurposeSelector } from "@/components/PurposeSelector";
import { emptyPurpose, isPurposeSelected, type PurposeSelection } from "@/lib/purpose";
import { resolveClientInitials, peekNextLoanNumber, formatLoanId } from "@/lib/loanId";
import { useAuth } from "@/hooks/useAuth";
import { upsertScenario, getScenario, reserveServerLoanSeq, markScenarioCommitted, type ScenarioStatus } from "@/lib/scenarios";
import { isCommitmentLimitError, COMMITMENT_LIMIT_MESSAGE } from "@/lib/entitlements";
import { canEditScenario } from "@/lib/scenarioAccess";
import { mergeHydratedSetup } from "@/lib/scenarioHydration";
import { useEditTracking } from "@/hooks/useEditTracking";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LumpSumScenarios, type LumpSumConfig, type PolicyLoanBalanceByPeriod } from "@/components/LumpSumScenarios";
import { LumpSumImpactCards } from "@/components/SummaryCards";
import { LumpSumChart } from "@/components/LumpSumChart";
import { InvestmentInterestReport } from "@/components/InvestmentInterestReport";
import { MultiPolicySnowball } from "@/components/MultiPolicySnowball";
import { solveForPayment, type SolveForPaymentResult } from "@/lib/paymentSolver";
import {
  buildAllLumpSumEvents,
  injectLumpSumsIntoPayments,
  buildStandardLoanWithLumpSums,
  calculateRedirectedFunding,
  type RedirectedFundingSummary,
} from "@/lib/lumpSumEngine";
import type { Jurisdiction } from "@/lib/formatters";

// Canada engine
import {
  runCombinedScenario as runCombinedScenarioCA,
  buildDashboardSummary as buildDashboardSummaryCA,
  buildPlannedRepaymentSchedule as buildPlannedScheduleCA,
  calculateActualPolicyLoan as calculateActualPolicyLoanCA,
  buildStandardLoanAmortization as buildStandardAmortCA,
  calculateFamilySystemRecapture as calculateRecaptureCA,
  toISODate,
  getPaymentsPerYear,
  type CombinedScenarioInput as CombinedScenarioInputCA,
  type CombinedScenarioResult as CombinedScenarioResultCA,
} from "@/lib/calculationEngine";

// USA engine (independent)
import {
  runCombinedScenario as runCombinedScenarioUS,
  buildDashboardSummary as buildDashboardSummaryUS,
  buildPlannedRepaymentSchedule as buildPlannedScheduleUS,
  calculateActualPolicyLoan as calculateActualPolicyLoanUS,
  buildStandardLoanAmortization as buildStandardAmortUS,
  calculateFamilySystemRecapture as calculateRecaptureUS,
  type CombinedScenarioInput as CombinedScenarioInputUS,
  type CombinedScenarioResult as CombinedScenarioResultUS,
} from "@/lib/usaLoanEngine";

const today = new Date();
const todayStr = toISODate(today);
const nextMonth = new Date(today);
nextMonth.setMonth(nextMonth.getMonth() + 1);
const nextMonthStr = toISODate(nextMonth);

const defaultSetup: LoanSetupData = {
  loanAmount: 30000,
  loanTitle: '',
  loanDate: todayStr,
  interestRate: 6.5,
  hasExistingBalance: false,
  consolidateExisting: false,
  existingBalance: 0,
  existingAccruedInterest: 0,
  availableCashValue: null,
  policyAnniversaryMonth: 7,
  policyAnniversaryDay: 18,
  policyNumber: '',
  lifeInsured: '',
  disciplineRate: 10,
  paymentFrequency: 'monthly',
  numberOfPayments: 60,
  termYears: 5,
  termMonths: 0,
  startDate: nextMonthStr,
  usaCarrier: 'generic',
  repaymentMode: null,
  desiredPayment: 0,
  precisionMode: false,
  isInvestmentPurpose: false,
  interestTrackingBasis: 'policy',
  interestOnlyBasis: 'policy',
  isMultiPolicy: false,
  multiPolicyCount: 2,
  policyLoans: [
    { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: 7, anniversaryDay: 18 },
    { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: 7, anniversaryDay: 18 },
    { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: 7, anniversaryDay: 18 },
    { amount: 0, startDate: '', policyNumber: '', useSameStartDate: true, useSameAnniversary: true, anniversaryMonth: 7, anniversaryDay: 18 },
  ],
};

const defaultLumpSumConfig: LumpSumConfig = {
  scenarios: [],
  continueAfterPayoff: null,
};

const Index = () => {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>('canada');
  const [mode, setMode] = useState<'advisor' | 'client'>('advisor');
  const [setup, setSetup] = useState<LoanSetupData>(defaultSetup);
  const [lumpSumConfig, setLumpSumConfig] = useState<LumpSumConfig>(defaultLumpSumConfig);
  const [lumpSumSectionOpen, setLumpSumSectionOpen] = useState(false);
  const [stewardship, setStewardship] = useState<StewardshipState>(emptyStewardship());
  const [futureMe, setFutureMe] = useState<FutureMeState>(emptyFutureMe());
  const [purpose, setPurpose] = useState<PurposeSelection>(emptyPurpose());
  const [policyOwnerName, setPolicyOwnerName] = useState("");
  const [loanBorrowerName, setLoanBorrowerName] = useState("");

  // Saved-scenario state
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [scenarioName, setScenarioName] = useState<string | null>(null);
  const [scenarioLoanId, setScenarioLoanId] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(true);
  const [scenarioStatus, setScenarioStatus] = useState<ScenarioStatus>("draft");
  const [hydrating, setHydrating] = useState(false);
  const [saving, setSaving] = useState(false);
  // Owner of the loaded scenario row; null until a saved scenario is loaded.
  // Advisors/admins can OPEN a client scenario but RLS rejects their writes —
  // the UI must treat that as view-only instead of autosaving into the void.
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const isOwner = canEditScenario(user?.id ?? null, ownerId);
  const hasEditedRef = useEditTracking(hydrating, [
    jurisdiction, mode, setup, lumpSumConfig, stewardship, futureMe, purpose,
    policyOwnerName, loanBorrowerName,
  ]);
  const saveErrorNotifiedRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  // Loan ID — initials resolved from the borrower / policy-owner names entered
  // in the Generate-Schedule card, with "XX" placeholder until a name exists.
  const loanId = useMemo(() => {
    const initials = resolveClientInitials(loanBorrowerName, policyOwnerName);
    return scenarioLoanId || formatLoanId(initials, peekNextLoanNumber(initials));
  }, [loanBorrowerName, policyOwnerName, scenarioLoanId]);

  const handleSetupChange = (newData: LoanSetupData) => {
    setSetup(newData);
  };

  // ---------- Load scenario from ?scenario=id ----------
  const requestedId = searchParams.get("scenario");
  useEffect(() => {
    if (!user || !requestedId || requestedId === scenarioId) return;
    setHydrating(true);
    getScenario(requestedId)
      .then((row) => {
        if (!row) {
          toast.error("Scenario not found");
          return;
        }
        const d = (row.data as Record<string, unknown>) || {};
        if (d.jurisdiction) setJurisdiction(d.jurisdiction as Jurisdiction);
        if (d.mode) setMode(d.mode as 'advisor' | 'client');
        if (d.setup) setSetup(prev => mergeHydratedSetup(prev, d.setup));
        if (d.lumpSumConfig) setLumpSumConfig(d.lumpSumConfig as LumpSumConfig);
        if (d.stewardship) setStewardship(normalizeStewardship(d.stewardship));
        if (d.futureMe) setFutureMe(d.futureMe as FutureMeState);
        if (d.purpose) setPurpose(d.purpose as PurposeSelection);
        setPolicyOwnerName((d.policyOwnerName as string) || "");
        setLoanBorrowerName((d.loanBorrowerName as string) || "");
        setScenarioId(row.id);
        setScenarioName(row.name);
        setScenarioLoanId(row.loan_id);
        setIsDraft(row.is_draft);
        setScenarioStatus(((row as unknown as { status?: ScenarioStatus }).status) ?? (row.is_draft ? "draft" : "saved"));
        setOwnerId(row.user_id ?? null);
        hasEditedRef.current = false;
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setHydrating(false));
  }, [user, requestedId, scenarioId]);

  // ---------- Auto-save draft (debounced) when signed in ----------
  const buildBlob = useCallback(() => ({
    v: 1,
    jurisdiction, mode, setup, lumpSumConfig,
    stewardship, futureMe, purpose, policyOwnerName, loanBorrowerName,
  }), [jurisdiction, mode, setup, lumpSumConfig, stewardship, futureMe, purpose, policyOwnerName, loanBorrowerName]);

  // Edit marking lives in useEditTracking: a plain mount or settling
  // hydration never arms autosave — only a real change after that does.

  useEffect(() => {
    if (!user || hydrating || !isOwner || !hasEditedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        setSaving(true);
        const row = await upsertScenario(user.id, {
          id: scenarioId,
          name: scenarioName,
          loan_id: scenarioLoanId,
          is_draft: isDraft,
          // Preserve current status on autosave — never downgrade saved/committed back to draft.
          status: scenarioStatus,
          data: buildBlob() as unknown as Parameters<typeof upsertScenario>[1]['data'],
        });
        if (!scenarioId) {
          setScenarioId(row.id);
          // reflect id in URL without re-triggering load
          setSearchParams({ scenario: row.id }, { replace: true });
        }
        saveErrorNotifiedRef.current = false;
      } catch (e) {
        console.warn("Auto-save failed", e);
        // One toast per failure streak — not one per keystroke.
        if (!saveErrorNotifiedRef.current) {
          saveErrorNotifiedRef.current = true;
          toast.error("Your changes aren't being saved", {
            description: e instanceof Error ? e.message : "Please check your connection and try again.",
          });
        }
      } finally {
        setSaving(false);
      }
    }, 1500);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [user, hydrating, isOwner, scenarioId, scenarioName, scenarioLoanId, isDraft, scenarioStatus, buildBlob, setSearchParams]);

  const handleSaveAs = async () => {
    if (!user) {
      toast.message("Sign in to save scenarios", { description: "Drafts and named scenarios sync to your account." });
      return;
    }
    const proposed = scenarioName || `Loan for ${loanBorrowerName || policyOwnerName || "client"}`;
    const name = window.prompt("Name this scenario:", proposed);
    if (!name) return;
    try {
      setSaving(true);
      // Reserve a permanent loan ID on first naming
      let finalLoanId = scenarioLoanId;
      if (!finalLoanId) {
        const seq = await reserveServerLoanSeq(user.id);
        const initials = resolveClientInitials(loanBorrowerName, policyOwnerName);
        finalLoanId = formatLoanId(initials, seq);
      }
      const row = await upsertScenario(user.id, {
        // A non-owner (advisor/admin viewing a client scenario) saves a COPY
        // under their own account — writing the original is blocked by RLS.
        id: isOwner ? scenarioId : null,
        name,
        loan_id: finalLoanId,
        is_draft: false,
        // Promote draft → saved scenario on explicit Save As.
        status: scenarioStatus === "committed" ? "committed" : "saved",
        data: buildBlob() as unknown as Parameters<typeof upsertScenario>[1]['data'],
      });
      setScenarioId(row.id);
      setOwnerId(user.id);
      setScenarioName(row.name);
      setScenarioLoanId(row.loan_id);
      setIsDraft(false);
      if (scenarioStatus !== "committed") setScenarioStatus("saved");
      if (!searchParams.get("scenario")) {
        setSearchParams({ scenario: row.id }, { replace: true });
      }
      toast.success("Scenario saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Solve for payment result
  const solvedPaymentResult = useMemo<SolveForPaymentResult | null>(() => {
    if (setup.repaymentMode !== 'payment' || !setup.desiredPayment || setup.desiredPayment <= 0 || !setup.loanAmount || setup.loanAmount <= 0) {
      return null;
    }
    const effectiveExisting = (setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingBalance : 0;
    const totalBorrowed = setup.loanAmount + effectiveExisting;
    return solveForPayment({
      desiredPayment: setup.desiredPayment,
      loanAmount: totalBorrowed,
      paymentFrequency: setup.paymentFrequency,
    });
  }, [setup.repaymentMode, setup.desiredPayment, setup.loanAmount, setup.hasExistingBalance, setup.consolidateExisting, setup.existingBalance, setup.paymentFrequency]);

  // Interest-only payment calculation
  const interestOnlyPayment = useMemo(() => {
    if (setup.repaymentMode !== 'interest_only') return 0;
    const effectiveExisting = (setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingBalance : 0;
    const totalBorrowed = setup.loanAmount + effectiveExisting;
    const rate = setup.interestOnlyBasis === 'policy' ? setup.interestRate / 100 : setup.disciplineRate / 100;
    const ppy = getPaymentsPerYear(setup.paymentFrequency);
    return totalBorrowed * rate / ppy;
  }, [setup.repaymentMode, setup.interestOnlyBasis, setup.interestRate, setup.disciplineRate, setup.loanAmount, setup.hasExistingBalance, setup.consolidateExisting, setup.existingBalance, setup.paymentFrequency]);

  // Determine effective discipline rate and term based on mode
  const effectiveRate = useMemo(() => {
    if (setup.repaymentMode === 'payment' && solvedPaymentResult?.found) {
      return solvedPaymentResult.solvedRate * 100;
    }
    return setup.disciplineRate;
  }, [setup.repaymentMode, setup.disciplineRate, solvedPaymentResult]);

  const effectiveTermCount = useMemo(() => {
    if (setup.repaymentMode === 'payment' && solvedPaymentResult?.found) {
      return solvedPaymentResult.solvedTermCount;
    }
    return setup.numberOfPayments;
  }, [setup.repaymentMode, setup.numberOfPayments, solvedPaymentResult]);

  // Resolve lump sum payment events
  const lumpSumEvents = useMemo(() => {
    return buildAllLumpSumEvents(
      lumpSumConfig.scenarios,
      lumpSumConfig.recurring,
      setup.startDate,
    );
  }, [lumpSumConfig.scenarios, lumpSumConfig.recurring, setup.startDate]);

  const hasActiveLumpSums = lumpSumEvents.length > 0;

  const results = useMemo(() => {
    if (!setup.loanAmount || setup.loanAmount <= 0) return null;
    if (setup.repaymentMode === null) return null;
    if (setup.repaymentMode === 'payment' && (!setup.desiredPayment || setup.desiredPayment <= 0)) return null;
    if (setup.repaymentMode === 'interest_only' && interestOnlyPayment <= 0) return null;

    const policyLoanInput = {
      jurisdiction: jurisdiction,
      loanAmount: setup.loanAmount,
      policyLoanAnnualRate: setup.interestRate / 100,
      policyLoanEffectiveDate: setup.loanDate,
      policyAnniversaryMonth: setup.policyAnniversaryMonth,
      policyAnniversaryDay: setup.policyAnniversaryDay,
      availableCashValue: setup.availableCashValue,
      existingPolicyLoanBalance: (setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingBalance : 0,
      existingAccruedInterest: (setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingAccruedInterest : 0,
      dayCountConvention: 'actual_actual' as const,
      rateChanges: [],
    };

    const effectiveExistingForRepayment = (setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingBalance : 0;

    const overridePayment = (setup.repaymentMode === 'payment' && setup.desiredPayment > 0) ? setup.desiredPayment : undefined;

    const repaymentStrategy = {
      borrowedAmount: setup.loanAmount + effectiveExistingForRepayment,
      targetAnnualRate: effectiveRate / 100,
      termCount: effectiveTermCount,
      paymentFrequency: setup.paymentFrequency,
      startDate: setup.startDate,
      overridePaymentAmount: overridePayment,
    };

    const scenarioInput = { policyLoanInput, repaymentStrategy };

    try {
      if (jurisdiction === 'usa') {
        // Base scenario (no lump sums)
        const combined = runCombinedScenarioUS(scenarioInput as CombinedScenarioInputUS);
        const dashboard = buildDashboardSummaryUS(combined);

        // With lump sums
        let lumpSumCombined: typeof combined | null = null;
        let lumpSumDashboard: typeof dashboard | null = null;
        let redirectedFunding: RedirectedFundingSummary | null = null;

        if (hasActiveLumpSums) {
          const basePlanned = buildPlannedScheduleUS(repaymentStrategy);
          const withLumpSums = injectLumpSumsIntoPayments(
            basePlanned as unknown as Parameters<typeof injectLumpSumsIntoPayments>[0],
            lumpSumEvents,
          );
          const projEnd = basePlanned.length > 0 ? basePlanned[basePlanned.length - 1].date : repaymentStrategy.startDate;

          const lsPolicy = calculateActualPolicyLoanUS({
            ...policyLoanInput,
            payments: withLumpSums as unknown as Parameters<typeof calculateActualPolicyLoanUS>[0]['payments'],
            projectionEndDate: projEnd,
          });

          const lsStandard = buildStandardLoanWithLumpSums(
            {
              principal: repaymentStrategy.borrowedAmount,
              annualRate: repaymentStrategy.targetAnnualRate,
              termCount: repaymentStrategy.termCount,
              paymentFrequency: repaymentStrategy.paymentFrequency,
              startDate: repaymentStrategy.startDate,
            },
            lumpSumEvents
          );

          const lsRecapture = calculateRecaptureUS({
            standardLoanResult: lsStandard as unknown as Parameters<typeof calculateRecaptureUS>[0]['standardLoanResult'],
            actualPolicyLoanResult: lsPolicy,
          });

          lumpSumCombined = {
            ...combined,
            actualPolicyLoan: lsPolicy,
            standardLoanComparison: lsStandard as unknown as typeof combined.standardLoanComparison,
            familySystemRecapture: lsRecapture,
          };
          lumpSumDashboard = buildDashboardSummaryUS(lumpSumCombined);

          if (lumpSumDashboard.earlyPayoffPeriods > 0) {
            redirectedFunding = calculateRedirectedFunding({
              originalPaymentCount: dashboard.standardLoanPaymentCount,
              updatedPaymentCount: lumpSumDashboard.policyLoanPaymentCount,
              paymentAmount: dashboard.targetPaymentPerPeriod,
              originalPayoffDate: dashboard.standardLoanPayoffDate,
              updatedPayoffDate: lumpSumDashboard.policyLoanPayoffDate || lumpSumDashboard.standardLoanPayoffDate,
              originalFinancingMargin: dashboard.additionalIntendedFinancingMargin,
              updatedFinancingMargin: lumpSumDashboard.additionalIntendedFinancingMargin,
            });
          }
        }

        return { combined, dashboard, scenarioInput, lumpSumCombined, lumpSumDashboard, redirectedFunding };
      } else {
        // Canada
        const combined = runCombinedScenarioCA(scenarioInput as CombinedScenarioInputCA);
        const dashboard = buildDashboardSummaryCA(combined);

        let lumpSumCombined: typeof combined | null = null;
        let lumpSumDashboard: typeof dashboard | null = null;
        let redirectedFunding: RedirectedFundingSummary | null = null;

        if (hasActiveLumpSums) {
          const basePlanned = buildPlannedScheduleCA(repaymentStrategy);
          const withLumpSums = injectLumpSumsIntoPayments(basePlanned, lumpSumEvents);
          const projEnd = basePlanned.length > 0 ? basePlanned[basePlanned.length - 1].date : repaymentStrategy.startDate;

          const lsPolicy = calculateActualPolicyLoanCA({
            ...policyLoanInput,
            payments: withLumpSums,
            projectionEndDate: projEnd,
          });

          const lsStandard = buildStandardLoanWithLumpSums(
            {
              principal: repaymentStrategy.borrowedAmount,
              annualRate: repaymentStrategy.targetAnnualRate,
              termCount: repaymentStrategy.termCount,
              paymentFrequency: repaymentStrategy.paymentFrequency,
              startDate: repaymentStrategy.startDate,
            },
            lumpSumEvents
          );

          const lsRecapture = calculateRecaptureCA({
            standardLoanResult: lsStandard,
            actualPolicyLoanResult: lsPolicy,
          });

          lumpSumCombined = {
            ...combined,
            actualPolicyLoan: lsPolicy,
            standardLoanComparison: lsStandard,
            familySystemRecapture: lsRecapture,
          };
          lumpSumDashboard = buildDashboardSummaryCA(lumpSumCombined);

          if (lumpSumDashboard.earlyPayoffPeriods > 0) {
            redirectedFunding = calculateRedirectedFunding({
              originalPaymentCount: dashboard.standardLoanPaymentCount,
              updatedPaymentCount: lumpSumDashboard.policyLoanPaymentCount,
              paymentAmount: dashboard.targetPaymentPerPeriod,
              originalPayoffDate: dashboard.standardLoanPayoffDate,
              updatedPayoffDate: lumpSumDashboard.policyLoanPayoffDate || lumpSumDashboard.standardLoanPayoffDate,
              originalFinancingMargin: dashboard.additionalIntendedFinancingMargin,
              updatedFinancingMargin: lumpSumDashboard.additionalIntendedFinancingMargin,
            });
          }
        }

        return { combined, dashboard, scenarioInput, lumpSumCombined, lumpSumDashboard, redirectedFunding };
      }
    } catch (err) {
      console.error('Calculation error:', err);
      return null;
    }
  }, [setup, jurisdiction, effectiveRate, effectiveTermCount, lumpSumEvents, hasActiveLumpSums, interestOnlyPayment]);

  const targetPayment = (setup.repaymentMode === 'payment' && setup.desiredPayment > 0) ? setup.desiredPayment : (results?.dashboard.targetPaymentPerPeriod ?? 0);

  // Determine the end date of the loan for lump sum validation
  const loanEndDate = results?.dashboard.standardLoanPayoffDate ?? null;

  // Build policy loan balance-by-period for lump sum hints
  const policyBalanceByPeriod = useMemo<PolicyLoanBalanceByPeriod[]>(() => {
    if (!results) return [];
    const ledger = results.combined.actualPolicyLoan.ledger;
    const payments = ledger.filter((row) => row.eventType === 'payment');
    return payments.map((row, i) => ({
      period: i + 1,
      balance: Math.max(0, Math.round(row.totalIndebtednessAfterEvent * 100) / 100),
    }));
  }, [results]);

  const policyPayoffPayment = useMemo(() => {
    if (!results) return undefined;
    const payoff = policyBalanceByPeriod.findIndex(p => p.balance < 0.01);
    return payoff >= 0 ? payoff + 1 : policyBalanceByPeriod.length;
  }, [results, policyBalanceByPeriod]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Family Policy Loan Planner — Calculate and compare policy loan repayments</title>
        <meta name="description" content="Calculate and compare policy loan repayments against standard financing. Track principal recapture, interest costs, and lump-sum impacts for Canada and USA." />
        <link rel="canonical" href="https://app.familyloanplanner.com/" />
        <meta property="og:title" content="Family Policy Loan Planner — Calculate and compare policy loan repayments" />
        <meta property="og:description" content="Calculate and compare policy loan repayments against standard financing. Track principal recapture, interest costs, and lump-sum impacts." />
        <meta property="og:url" content="https://app.familyloanplanner.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Family Policy Loan Planner",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "https://app.familyloanplanner.com/",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" }
        })}</script>
      </Helmet>
      <AppHeader
        jurisdiction={jurisdiction}
        mode={mode}
        theme={theme}
        onJurisdictionChange={setJurisdiction}
        onModeChange={setMode}
        onToggleTheme={toggleTheme}
      />

      <main className="container mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6 sm:space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card p-3">
          <div className="flex items-center gap-2 text-sm min-w-0">
            {user ? (
              <>
                <Badge variant="outline" className="font-mono text-xs">{loanId}</Badge>
                <span className="truncate font-medium">{scenarioName || "Untitled draft"}</span>
                {isDraft && <Badge variant="secondary">Draft</Badge>}
                {!isOwner && (
                  <span className="text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
                    View only
                  </span>
                )}
                {saving && <span className="text-xs text-muted-foreground">Saving…</span>}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                <a href="/auth" className="underline">Sign in</a> to auto-save drafts and revisit scenarios.
              </span>
            )}
          </div>
          {user && (
            <Button size="sm" onClick={handleSaveAs} disabled={saving}>
              Save as named scenario
            </Button>
          )}
        </div>

        <section>
          <h2 className="text-lg font-semibold mb-4">Loan Setup</h2>
          <div className="mb-4">
            <PurposeSelector
              loanTitle={setup.loanTitle}
              onLoanTitleChange={(s) => setSetup({ ...setup, loanTitle: s })}
              purpose={purpose}
              onPurposeChange={setPurpose}
            />
          </div>
          <LoanSetup
            data={setup}
            onChange={handleSetupChange}
            targetPayment={targetPayment}
            jurisdiction={jurisdiction}
            solvedPayment={solvedPaymentResult}
            loanId={loanId}
          />
        </section>

        {/* USA Carrier Notes */}
        {jurisdiction === 'usa' && (
          <section>
            <USACarrierNotes carrier={setup.usaCarrier} paymentFrequency={setup.paymentFrequency} />
          </section>
        )}


        {results && (
          <>
            <section>
              <LoanHeaderCard
                loanName={scenarioName}
                loanId={loanId}
                status={scenarioStatus}
                dashboard={results.dashboard}
                purpose={purpose}
                jurisdiction={jurisdiction}
                progressPct={(() => {
                  // Best-effort progress until per-loan actuals are wired here.
                  // Committed = at least the planning phase is done (25% floor); Saved = 10%; Draft = 0%.
                  if (scenarioStatus === "committed") return 25;
                  if (scenarioStatus === "saved") return 10;
                  return 0;
                })()}
              />
            </section>

            <section>
              {(() => {
                const milestones: MilestoneStep[] = [
                  { key: "created", label: "Loan Created", state: "done" },
                  {
                    key: "commitment",
                    label: "Commitment Agreement",
                    state: scenarioStatus === "committed" ? "done" : "current",
                  },
                  {
                    key: "stewardship",
                    label: "Stewardship (optional)",
                    state: stewardshipCompleted(stewardship) ? "done" : "todo",
                  },
                  { key: "first_payment", label: "First Payment", state: "todo" },
                  { key: "lump_sum", label: "Lump Sum Applied", state: hasActiveLumpSums ? "done" : "todo" },
                  { key: "repaid", label: "Loan Repaid", state: "todo" },
                  { key: "redirected", label: "Margin™ Redirected", state: "todo" },
                ];
                return <LoanProgressStrip steps={milestones} />;
              })()}
            </section>

            <section>
              <h2 className="text-lg font-semibold mb-4">Summary</h2>
              <GroupedMetrics
                summary={results.dashboard}
                lumpSumSummary={results.lumpSumDashboard}
                jurisdiction={jurisdiction}
                isInterestOnly={setup.repaymentMode === 'interest_only'}
              />
              <div className="mt-4">
                <SummaryCards
                  summary={results.dashboard}
                  jurisdiction={jurisdiction}
                  desiredPayment={setup.repaymentMode === 'payment' ? setup.desiredPayment : undefined}
                  isInterestOnly={setup.repaymentMode === 'interest_only'}
                />
              </div>
            </section>

            <section>
              <IdeasPanel />
            </section>

            <section>
              <BalanceChart
                policyLedger={results.combined.actualPolicyLoan.ledger}
                amortization={results.combined.standardLoanComparison.amortizationSchedule}
              />
            </section>

            {setup.repaymentMode !== 'interest_only' && (
              <section>
                <CostComparison recapture={results.combined.familySystemRecapture} jurisdiction={jurisdiction} />
              </section>
            )}

            {/* Lump Sum Scenarios — Advanced Area (below Cost Comparison) */}
            <section>
              <LumpSumScenarios
                config={lumpSumConfig}
                onChange={setLumpSumConfig}
                jurisdiction={jurisdiction}
                loanStartDate={setup.startDate}
                loanEndDate={loanEndDate}
                isOpen={lumpSumSectionOpen}
                onOpenChange={setLumpSumSectionOpen}
                policyBalanceByPeriod={policyBalanceByPeriod}
                policyPayoffPayment={policyPayoffPayment}
              />

              {/* Lump Sum Impact cards & chart — inside the lump sum area */}
              {lumpSumSectionOpen && hasActiveLumpSums && results.lumpSumDashboard && (
                <LumpSumImpactCards
                  summary={results.dashboard}
                  lumpSumDashboard={results.lumpSumDashboard}
                  redirectedFunding={results.redirectedFunding ?? undefined}
                  continueAfterPayoff={lumpSumConfig.continueAfterPayoff}
                  jurisdiction={jurisdiction}
                />
              )}

              {lumpSumSectionOpen && hasActiveLumpSums && results.lumpSumCombined && (
                <div className="mt-4">
                  <LumpSumChart
                    originalPolicyLedger={results.combined.actualPolicyLoan.ledger}
                    lumpSumPolicyLedger={results.lumpSumCombined.actualPolicyLoan.ledger}
                    standardAmortization={results.combined.standardLoanComparison.amortizationSchedule}
                    jurisdiction={jurisdiction}
                  />
                </div>
              )}
            </section>

            {/* Multi-Policy Snowball */}
            {setup.isMultiPolicy && (
              <section>
                <MultiPolicySnowball
                  isMultiPolicy={setup.isMultiPolicy}
                  policyCount={setup.multiPolicyCount}
                  policyInputs={setup.policyLoans}
                  totalLoanAmount={setup.loanAmount + ((setup.hasExistingBalance && setup.consolidateExisting) ? setup.existingBalance : 0)}
                  paymentAmount={targetPayment}
                  paymentFrequency={setup.paymentFrequency}
                  firstPaymentDate={setup.startDate}
                  policyLoanRate={setup.interestRate / 100}
                  jurisdiction={jurisdiction}
                />
              </section>
            )}

            {/* Investment Interest Report */}
            {setup.isInvestmentPurpose && (
              <section>
                <InvestmentInterestReport
                  policyLedger={results.combined.actualPolicyLoan.ledger}
                  amortization={results.combined.standardLoanComparison.amortizationSchedule}
                  interestBasis={setup.interestTrackingBasis}
                  jurisdiction={jurisdiction}
                />
              </section>
            )}

            <section>
              <ScheduleTabs
                policyLedger={results.combined.actualPolicyLoan.ledger}
                amortization={results.combined.standardLoanComparison.amortizationSchedule}
                plannedPayments={results.combined.plannedPayments}
                mode={mode}
                jurisdiction={jurisdiction}
                policyNumber={setup.policyNumber}
                loanTitle={setup.loanTitle}
              />
            </section>

            <section>
              <PrintSchedule
                loanSetup={setup}
                combined={results.combined}
                scenarioId={scenarioId}
                policyLoanInput={results.scenarioInput.policyLoanInput}
                repaymentStrategy={results.scenarioInput.repaymentStrategy}
                jurisdiction={jurisdiction}
                solvedPayment={setup.repaymentMode === 'payment' ? solvedPaymentResult : null}
                dashboard={results.dashboard}
                loanId={loanId}
                stewardship={stewardship}
                futureMe={futureMe}
                policyOwnerName={policyOwnerName}
                onPolicyOwnerNameChange={setPolicyOwnerName}
                loanBorrowerName={loanBorrowerName}
                onLoanBorrowerNameChange={setLoanBorrowerName}
                purpose={purpose}
                lumpSumConfig={lumpSumConfig}
                lumpSumCombined={results.lumpSumCombined}
                lumpSumDashboard={results.lumpSumDashboard}
                onCommitmentGenerated={async () => {
                  // Promote scenario to "committed" the first time a Commitment Agreement is generated.
                  if (!user || !scenarioId || !isOwner || scenarioStatus === "committed") return;
                  try {
                    await markScenarioCommitted(scenarioId);
                    setScenarioStatus("committed");
                    setIsDraft(false);
                  } catch (e) {
                    if (isCommitmentLimitError(e)) {
                      toast(COMMITMENT_LIMIT_MESSAGE, {
                        duration: 12000,
                        action: {
                          label: "Join the paid beta list",
                          onClick: () => { window.location.href = "/about#paid-plans"; },
                        },
                      });
                    } else {
                      console.warn("Failed to mark committed", e);
                      toast.error("Couldn't mark this scenario as committed", {
                        description: "The document was generated, but the status didn't update. Please try again.",
                      });
                    }
                  }
                }}
              />
            </section>

            <section>
              <StewardshipAndFutureMe
                stewardship={stewardship}
                onStewardshipChange={setStewardship}
                futureMe={futureMe}
                onFutureMeChange={setFutureMe}
                autoContext={{
                  purposeSelected: isPurposeSelected(purpose),
                  scheduleCreated: true,
                  paymentEstablished: targetPayment > 0,
                  timelineEstablished: effectiveTermCount > 0,
                  marginIdentified: (results.dashboard.additionalIntendedFinancingMargin ?? 0) > 0,
                } as StewardshipAuto}
              />
            </section>

            <section>
              {(() => {
                const ms: Milestone[] = [
                  { key: "purpose", label: "Loan purpose defined", done: isPurposeSelected(purpose) },
                  { key: "payment", label: "Target payment established", done: targetPayment > 0 },
                  { key: "timeline", label: "Repayment timeline set", done: effectiveTermCount > 0 },
                  { key: "margin", label: "Family Financing Margin™ identified", done: (results.dashboard.additionalIntendedFinancingMargin ?? 0) > 0 },
                  { key: "committed", label: "Commitment Agreement generated", done: scenarioStatus === "committed" },
                  { key: "stewardship", label: "Stewardship Assessment completed", done: stewardshipCompleted(stewardship) },
                ];
                return <MilestonesPanel milestones={ms} />;
              })()}
            </section>

            <section>
              <StressTestAnalysis
                scenarioInput={results.scenarioInput}
                combined={results.combined}
                jurisdiction={jurisdiction}
              />
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default Index;
